import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TbEdit, TbExternalLink, TbPlus } from "react-icons/tb";
import { ScreenGreeting } from "../../../Components/Molecules";
import { OperatorsSkeleton } from "../../../Components/Molecules/DashboardSkeleton";
import Tooltip from "../../../Components/Atoms/Tooltip";
import api from "../../../Services/api";
import { StatusBadge } from "../Organizers/StatusBadge";
import CreateOperatorModal from "./CreateOperatorModal";

const TipoUser = {
  admin: "Administrador",
  user: "Usuário",
  subAccount: "Operador",
};

const GRID_COLS_WITH_POS =
  "lg:grid-cols-[minmax(0,2.2fr)_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)_40px]";
const GRID_COLS_WITHOUT_POS =
  "lg:grid-cols-[minmax(0,2.2fr)_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)_40px]";

const TableHeader = ({ showPosColumn, gridCols }) => (
  <div
    className={`${showPosColumn ? "col-span-7" : "col-span-6"} hidden border-b border-gray-100 bg-gray-50/70 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-300 lg:-mx-4 lg:grid lg:grid-cols-subgrid lg:items-center lg:gap-x-4 lg:px-4 ${gridCols}`}
  >
    <span>Nome</span>
    <span>E-mail</span>
    <span>Perfil</span>
    {showPosColumn ? <span>Ponto de venda</span> : null}
    <span className="text-center">Criação</span>
    <span className="text-center">Status</span>
    <span className="sr-only">Ações</span>
  </div>
);

const GridCell = ({ label, children, align = "left" }) => (
  <div>
    <p
      className={`mb-px text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only ${
        align === "right" ? "text-right" : align === "center" ? "text-center" : ""
      }`}
    >
      {label}
    </p>
    <div
      className={
        align === "right" ? "text-right" : align === "center" ? "flex justify-center" : ""
      }
    >
      {children}
    </div>
  </div>
);

export const OperatorsTableList = ({ operators, isHiddenSubAccountLink, onEdit }) => {
  const showPosColumn = !isHiddenSubAccountLink;
  const gridCols = showPosColumn ? GRID_COLS_WITH_POS : GRID_COLS_WITHOUT_POS;

  if (!operators?.length) {
    return (
      <div className="app-empty-state m-4">Nenhum operador foi adicionado.</div>
    );
  }

  return (
    <div className={`lg:grid ${gridCols} lg:gap-x-4 lg:px-4`}>
      <TableHeader showPosColumn={showPosColumn} gridCols={gridCols} />
      {operators.map((row) => (
        <div
          key={row.id}
          className={`flex flex-col gap-2 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-brand-dark-border lg:grid lg:grid-cols-subgrid lg:items-center lg:px-0 lg:py-2.5 ${
            showPosColumn ? "lg:col-span-7" : "lg:col-span-6"
          }`}
        >
          <GridCell label="Nome">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">{row.name}</p>
          </GridCell>
          <GridCell label="E-mail">
            <p className="break-all text-sm text-gray-600 dark:text-gray-300">{row.email}</p>
          </GridCell>
          <GridCell label="Perfil">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {TipoUser[row.profile] || row.profile}
            </p>
          </GridCell>
          {showPosColumn ? (
            <GridCell label="Ponto de venda">
              {row.subAccountId ? (
                <Link
                  to={`/pontos-de-venda/${row.subAccountId}`}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[#c09455] hover:underline dark:text-brand-gold-mid"
                >
                  Ver ponto de venda
                  <TbExternalLink className="h-3.5 w-3.5" />
                </Link>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </GridCell>
          ) : null}
          <GridCell label="Criação" align="center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {new Date(row.createdAt).toLocaleDateString("pt-BR")}
            </p>
          </GridCell>
          <GridCell label="Status" align="center">
            <StatusBadge active={row.isActive} />
          </GridCell>
          <div className="flex justify-end lg:justify-center">
            <Tooltip title="Editar operador">
              <button
                type="button"
                className="btn-outline-icon"
                onClick={() => onEdit?.(row.id)}
                aria-label={`Editar operador ${row.name}`}
              >
                <TbEdit className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  );
};

export const Operators = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const getOperators = async () => {
    try {
      const { data } = await api.get("/operators");
      setOperators(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOperators();
  }, []);

  if (loading) {
    return (
      <div className="profile-page w-full min-w-0 max-w-full">
        <ScreenGreeting
          title="Operadores"
          subTitle="Aqui você poderá realizar a gestão dos seus operadores."
        />
        <OperatorsSkeleton />
      </div>
    );
  }

  return (
    <div className="profile-page w-full min-w-0 max-w-full">
      <ScreenGreeting
        title="Operadores"
        subTitle="Aqui você poderá realizar a gestão dos seus operadores."
      />

      <section className="mb-5">
        <p className="dashboard-section-label mb-3">Ações</p>
        <button
          type="button"
          className="btn-primary w-full sm:w-auto"
          onClick={() => {
            setEditingId(null);
            setOpenCreate(true);
          }}
        >
          <TbPlus className="h-4 w-4" />
          Adicionar operador
        </button>
      </section>

      <CreateOperatorModal
        open={openCreate}
        operatorId={editingId || undefined}
        onClose={() => {
          setOpenCreate(false);
          setEditingId(null);
        }}
        onSaved={() => {
          getOperators();
        }}
      />

      <section className="min-w-0">
        <p className="dashboard-section-label mb-3">Lista de operadores</p>
        <div className="app-card overflow-hidden">
          <div className="border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border">
            <p className="text-sm font-bold text-brand-dark dark:text-gray-100">
              Operadores cadastrados
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Gerencie perfis, status e vínculos com pontos de venda.
            </p>
          </div>
          <OperatorsTableList
            operators={operators}
            onEdit={(id) => {
              setEditingId(id);
              setOpenCreate(true);
            }}
          />
        </div>
      </section>
    </div>
  );
};
